import { useState } from 'react';
import { ArrowDown, ArrowUp, Plus } from 'lucide-react';
import type { UserWatchlist } from '@/lib/watchlists';
import { moveItem, watchlistIconClass, watchlistInputClass } from './watchlistEditor.utils';

interface WatchlistEditorSidebarProps {
  watchlists: readonly UserWatchlist[];
  selectedId: string | null;
  maxWatchlists: number;
  maxNameLength: number;
  isMutating: boolean;
  onSelectedIdChange: (watchlistId: string) => void;
  onCreate: (name: string) => Promise<UserWatchlist>;
  onReorder: (watchlistIds: readonly string[]) => Promise<unknown>;
}

export function WatchlistEditorSidebar({
  watchlists,
  selectedId,
  maxWatchlists,
  maxNameLength,
  isMutating,
  onSelectedIdChange,
  onCreate,
  onReorder,
}: WatchlistEditorSidebarProps) {
  const [newName, setNewName] = useState('');
  const orderedIds = watchlists.map((watchlist) => watchlist.id);
  const run = (operation: Promise<unknown>) => void operation.catch(() => {});

  return (
    <aside className="border-b border-zinc-800 p-3 min-[720px]:border-b-0 min-[720px]:border-r">
      <div className="mb-3 flex gap-2">
        <input
          aria-label="New watchlist name"
          value={newName}
          maxLength={maxNameLength}
          onChange={(event) => setNewName(event.target.value)}
          placeholder="New watchlist"
          className={watchlistInputClass}
        />
        <button
          type="button"
          aria-label="Create watchlist"
          disabled={isMutating || newName.trim() === '' || watchlists.length >= maxWatchlists}
          onClick={() =>
            run(
              onCreate(newName).then((created) => {
                setNewName('');
                onSelectedIdChange(created.id);
              }),
            )
          }
          className={watchlistIconClass}
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-1">
        {watchlists.map((watchlist, index) => {
          const isPending = watchlist.id.startsWith('__pending_watchlist_');

          return (
            <div key={watchlist.id} className="flex items-center gap-1">
              <button
                type="button"
                disabled={isPending}
                aria-busy={isPending}
                onClick={() => onSelectedIdChange(watchlist.id)}
                className={`h-9 min-w-0 flex-1 truncate rounded px-2 text-left font-mono text-[10px] disabled:cursor-wait disabled:opacity-60 ${
                  watchlist.id === selectedId
                    ? 'bg-white/[.1] text-zinc-100'
                    : 'text-zinc-400 hover:bg-white/[.05]'
                }`}
              >
                {watchlist.name}
              </button>
              <OrderButton
                label={`Move ${watchlist.name} up`}
                disabled={isMutating || index === 0}
                direction="up"
                onClick={() => run(onReorder(moveItem(orderedIds, index, index - 1)))}
              />
              <OrderButton
                label={`Move ${watchlist.name} down`}
                disabled={isMutating || index === orderedIds.length - 1}
                direction="down"
                onClick={() => run(onReorder(moveItem(orderedIds, index, index + 1)))}
              />
            </div>
          );
        })}
      </div>
    </aside>
  );
}

function OrderButton({
  label,
  direction,
  disabled,
  onClick,
}: {
  label: string;
  direction: 'up' | 'down';
  disabled: boolean;
  onClick: () => void;
}) {
  const Icon = direction === 'up' ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={watchlistIconClass}
    >
      <Icon className="h-3 w-3" />
    </button>
  );
}
